import React from 'react';
import { X, User, Package, Calendar, FileText, Clock } from 'lucide-react';

const OrderDetailsModal = ({ order, language, onClose }) => {
  const labels = {
    title: { en: 'Order Details', ar: 'تفاصيل الطلب' },
    orderNumber: { en: 'Order Number', ar: 'رقم الطلب' },
    customer: { en: 'Customer', ar: 'العميل' },
    phone: { en: 'Phone', ar: 'الهاتف' },
    products: { en: 'Products', ar: 'المنتجات' },
    quantity: { en: 'Qty', ar: 'الكمية' },
    price: { en: 'Price', ar: 'السعر' },
    subtotal: { en: 'Subtotal', ar: 'المجموع الفرعي' },
    total: { en: 'Total', ar: 'الإجمالي' },
    status: { en: 'Status', ar: 'الحالة' },
    orderDate: { en: 'Order Date', ar: 'تاريخ الطلب' },
    notes: { en: 'Notes', ar: 'الملاحظات' },
    timeline: { en: 'Order Timeline', ar: 'سجل الطلب' },
    close: { en: 'Close', ar: 'إغلاق' },
    noNotes: { en: 'No notes', ar: 'لا توجد ملاحظات' }
  };

  const calculateTotal = () => {
    return order?.products?.reduce((sum, p) => sum + (p?.price || 0) * (p?.quantity || 0), 0) || 0;
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-gray-900 border border-[#FFD700]/30 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-[#FFD700]/20 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-[#FFD700]">{labels?.title?.[language]}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order Number & Status */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">{labels?.orderNumber?.[language]}</p>
                <p className="text-white font-bold text-lg">{order?.id}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">{labels?.status?.[language]}</p>
                <p className="text-[#FFD700] font-bold text-lg">{order?.status}</p>
              </div>
            </div>
          </div>

          {/* Customer Info */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-5 h-5 text-[#FFD700]" />
              <h3 className="text-white font-bold">{labels?.customer?.[language]}</h3>
            </div>
            <div className="space-y-2 text-gray-300">
              <p>{order?.customer?.name}</p>
              <p className="text-sm text-gray-400">{order?.customer?.phone}</p>
            </div>
          </div>

          {/* Products */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5 text-[#FFD700]" />
              <h3 className="text-white font-bold">{labels?.products?.[language]}</h3>
            </div>
            <div className="space-y-3">
              {order?.products?.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                  <div className="flex-1">
                    <p className="text-white font-medium">{product?.name}</p>
                    <p className="text-sm text-gray-400">
                      {labels?.quantity?.[language]}: {product?.quantity} × {product?.price} {language === 'ar' ? 'ريال' : 'SAR'}
                    </p>
                  </div>
                  <div className="text-[#FFD700] font-bold">
                    {(product?.price || 0) * (product?.quantity || 0)} {language === 'ar' ? 'ريال' : 'SAR'}
                  </div>
                </div>
              ))}
              <div className="pt-3 border-t border-[#FFD700]/30 flex items-center justify-between">
                <span className="text-white font-bold text-lg">{labels?.total?.[language]}</span>
                <span className="text-[#FFD700] font-bold text-xl">
                  {calculateTotal()} {language === 'ar' ? 'ريال' : 'SAR'}
                </span>
              </div>
            </div>
          </div>

          {/* Order Date */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-[#FFD700]" />
              <h3 className="text-white font-bold">{labels?.orderDate?.[language]}</h3>
            </div>
            <p className="text-gray-300">
              {new Date(order.orderDate)?.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {/* Notes */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-[#FFD700]" />
              <h3 className="text-white font-bold">{labels?.notes?.[language]}</h3>
            </div>
            <p className="text-gray-300">
              {order?.notes || <span className="text-gray-500 italic">{labels?.noNotes?.[language]}</span>}
            </p>
          </div>

          {/* Timeline */}
          <div className="bg-black/50 border border-[#FFD700]/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#FFD700]" />
              <h3 className="text-white font-bold">{labels?.timeline?.[language]}</h3>
            </div>
            <div className="space-y-3">
              {order?.timeline?.map((entry, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#FFD700] rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-white font-medium">{entry?.status}</p>
                    <p className="text-sm text-gray-400">
                      {new Date(entry.timestamp)?.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                    </p>
                    <p className="text-sm text-gray-500">{entry?.admin}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-[#FFD700]/20 p-6">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-[#FFD700] text-black rounded-lg hover:bg-[#FFD700]/90 transition-colors font-bold"
          >
            {labels?.close?.[language]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;